import { useState } from "react";
import { toast } from "sonner";
import { ScanFace, Loader2, RefreshCw } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

export const applyFace = (c, fc) => (c.aspect === "9:16" && fc?.found
  ? { ...c, auto_face: true, fit: "split", cam_rect: fc.rect, cam_x: 0.5, cam_y: 0.5, cam_zoom: 1, crop_y: 0.5 }
  : { ...c, auto_face: false });

export const FacePanel = ({ video, cfg, setCfg, onFacecam }) => {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const fc = video.facecam;
  const vertical = cfg.aspect === "9:16";
  const detect = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/videos/${video.id}/facecam`, { redetect: true });
      onFacecam?.(data.facecam);
      if (data.facecam.found) { setCfg((c) => applyFace(c, data.facecam)); toast.success(t("face.applied")); } else toast(t("face.none"));
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const status = fc == null ? t("face.scanning") : fc.found ? `${t("face.found")} · ${Math.round((fc.conf || 0) * 100)}%` : t("face.none");
  return (
    <div className={`rounded-xl border p-3 space-y-2 ${cfg.auto_face ? "border-[#3ad7ff]/50 bg-[#3ad7ff]/[0.06]" : "border-white/10 bg-white/[0.02]"}`} data-testid="face-panel">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <ScanFace className="w-4 h-4 text-[#3ad7ff] shrink-0" />
          <div className="min-w-0">
            <div className="text-xs text-white font-semibold">{t("face.title")}</div>
            <div className={`text-[10px] ${fc?.found ? "text-[#3ad7ff]" : "text-slate-500"}`} data-testid="face-status">{status}</div>
          </div>
        </div>
        <Switch data-testid="face-auto-switch" checked={!!cfg.auto_face && vertical} disabled={!fc?.found || !vertical}
          onCheckedChange={(v) => setCfg((c) => (v ? applyFace(c, fc) : { ...c, auto_face: false }))} />
      </div>
      <p className="text-[10px] text-slate-400">{vertical ? t("face.desc") : t("face.only916")}</p>
      <button type="button" onClick={detect} disabled={busy} data-testid="face-detect-btn" className="cx-metal rounded-full px-3 py-1.5 text-[11px] flex items-center gap-1.5">
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} {t("face.detect")}
      </button>
    </div>
  );
};
